// src/utils/generateInvoicePDF.js
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import SiteLogo from '../assets/images/SparkLogistics.jpg';
import BASE_URL from '../api';
import { toast } from 'react-toastify';

const API_BASE = `${BASE_URL}/api`;

// ────── GST STATE CODES MAPPING (INDIA) ──────
const GST_STATE_CODES = {
  '01': 'Jammu & Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '26': 'Dadra & Nagar Haveli and Daman & Diu',
  '27': 'Maharashtra',
  '28': 'Andhra Pradesh',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman & Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh (New)',
  '38': 'Ladakh'
};

// ────── NUMBER TO WORDS (INDIAN NUMBERING SYSTEM) ──────
export const numberToWordsIndian = (num) => {
  const nVal = Number(num);
  if (isNaN(nVal) || nVal === 0) return 'INR Zero Only';

  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertLessThanOneThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
    return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + convertLessThanOneThousand(n % 100) : '');
  };

  let intPart = Math.floor(Math.abs(nVal));
  const paise = Math.round((Math.abs(nVal) - intPart) * 100);

  let words = '';
  if (intPart >= 10000000) {
    const crores = Math.floor(intPart / 10000000);
    words += convertLessThanOneThousand(crores) + ' Crore ';
    intPart %= 10000000;
  }
  if (intPart >= 100000) {
    const lakhs = Math.floor(intPart / 100000);
    words += convertLessThanOneThousand(lakhs) + ' Lakh ';
    intPart %= 100000;
  }
  if (intPart >= 1000) {
    const thousands = Math.floor(intPart / 1000);
    words += convertLessThanOneThousand(thousands) + ' Thousand ';
    intPart %= 1000;
  }
  if (intPart > 0) {
    words += convertLessThanOneThousand(intPart);
  }

  let result = 'INR ' + words.trim();
  if (paise > 0) {
    result += ' and ' + convertLessThanOneThousand(paise) + ' Paise';
  }
  return result + ' Only';
};

// ────── CHECK IF STRING IS MONGODB OBJECTID ──────
const isMongoId = (str) => typeof str === 'string' && /^[0-9a-fA-F]{24}$/.test(str.trim());

// ────── CLEAN AND INTELLIGENT ADDRESS FORMATTER ──────
export const cleanClientAddress = (client) => {
  if (!client) return 'Industrial Estate, Chennai, Tamil Nadu - 600032';

  const parts = [];

  const addPart = (val) => {
    if (!val) return;
    if (typeof val === 'object') {
      if (val.address && !isMongoId(val.address)) parts.push(val.address.trim());
      if (val.areaname && !isMongoId(val.areaname)) parts.push(val.areaname.trim());
      if (val.hub && !isMongoId(val.hub)) parts.push(val.hub.trim());
      if (val.station && !isMongoId(val.station)) parts.push(val.station.trim());
      if (val.pincode && !isMongoId(val.pincode)) parts.push(String(val.pincode).trim());
      return;
    }
    const s = String(val).trim();
    if (s && !isMongoId(s) && !parts.includes(s)) {
      parts.push(s);
    }
  };

  addPart(client.address);
  addPart(client.area);
  addPart(client.region);
  addPart(client.station);

  let pin = '';
  if (client.pincode) {
    if (typeof client.pincode === 'object' && client.pincode.pincode) {
      pin = String(client.pincode.pincode).trim();
    } else if (typeof client.pincode === 'string' && !isMongoId(client.pincode)) {
      pin = client.pincode.trim();
    }
  }
  if (pin && !parts.some(p => p.includes(pin))) {
    parts.push(`PIN: ${pin}`);
  }

  // If no valid address parts were found (e.g. were all unpopulated ObjectIds)
  if (parts.length === 0) {
    const comp = (client.companyName || client.name || '').toLowerCase();
    if (comp.includes('thoraipakkam')) {
      return 'Thoraipakkam Industrial Area, OMR Road, Chennai, Tamil Nadu - 600097';
    } else if (comp.includes('guindy')) {
      return 'Guindy Industrial Estate, Chennai, Tamil Nadu - 600032';
    } else if (comp.includes('ambattur')) {
      return 'Ambattur Industrial Estate, Chennai, Tamil Nadu - 600058';
    }
    return 'Registered Commercial Premises, Chennai, Tamil Nadu';
  }

  return parts.join(', ');
};

// ────── ROBUST LOGO BASE64 LOADER ──────
export const loadLogoBase64 = (url) => {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width || 360;
        canvas.height = img.naturalHeight || img.height || 180;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
        resolve(dataUrl);
      } catch (err) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

// ────── MAIN PROFESSIONAL TAX INVOICE GENERATOR ──────
export const generateInvoicePDF = async (inv, token, externalToastId = null) => {
  const toastId = externalToastId || toast.loading('Generating professional tax invoice PDF...');

  try {
    const client = inv.clientId || {};
    const logoData = await loadLogoBase64(SiteLogo);

    // 1. ATTEMPT TO FETCH DETAILED DOCKETS FOR THIS INVOICE PERIOD
    let matchedDockets = [];
    try {
      const dRes = await fetch(`${API_BASE}/getctrl/getalldocketconfirmation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const dJson = await dRes.json();

      if (dJson.success && Array.isArray(dJson.data)) {
        const cId = String(client._id || client.id || client || '').trim();
        const cCompName = String(client.companyName || '').trim().toLowerCase();
        const cPersonName = String(client.name || '').trim().toLowerCase();

        // Parse date bounds (inclusive start-of-day and end-of-day)
        let fTime = 0;
        let tTime = Infinity;
        if (inv.fromDate) {
          const fD = new Date(inv.fromDate);
          fD.setHours(0, 0, 0, 0);
          fTime = fD.getTime();
        }
        if (inv.toDate) {
          const tD = new Date(inv.toDate);
          tD.setHours(23, 59, 59, 999);
          tTime = tD.getTime();
        }

        // Parse bill month / year if available (e.g. "Aug 2026")
        let billMonthIdx = -1;
        let billYear = -1;
        if (inv.billMonth) {
          const parts = inv.billMonth.trim().split(/\s+/);
          if (parts.length >= 2) {
            const mStr = parts[0].toLowerCase();
            const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            billMonthIdx = months.findIndex(m => mStr.startsWith(m));
            billYear = parseInt(parts[1], 10);
          }
        }

        matchedDockets = dJson.data.filter(d => {
          // Check matching client ID
          const dCustId = String(
            d.consigneeid?._id || d.consigneeid?.id || d.consigneeid ||
            d.clientid?._id || d.clientid?.id || d.clientid ||
            d.consignorid?._id || d.consignorid?.id || d.consignorid || ''
          ).trim();

          const dConsigneeName = String(d.consigneeid?.companyName || d.consigneeid?.name || d.consignee || '').trim().toLowerCase();
          const dClientName = String(d.clientid?.companyName || d.clientid?.name || '').trim().toLowerCase();
          const dConsignorName = String(d.consignorid?.companyName || d.consignorid?.name || d.consignor || '').trim().toLowerCase();

          const idMatches = cId && dCustId === cId;
          const nameMatches =
            (cCompName && (dConsigneeName.includes(cCompName) || cCompName.includes(dConsigneeName) || dClientName.includes(cCompName) || cCompName.includes(dClientName))) ||
            (cPersonName && (dConsigneeName.includes(cPersonName) || dConsignorName.includes(cPersonName) || dClientName.includes(cPersonName)));

          if (!idMatches && !nameMatches) return false;

          // Date check
          const dTime = d.bookingdate ? new Date(d.bookingdate).getTime() : 0;
          if (fTime || tTime !== Infinity) {
            return (!fTime || dTime >= fTime) && (!tTime || dTime <= tTime);
          } else if (billMonthIdx !== -1 && billYear !== -1 && d.bookingdate) {
            const dD = new Date(d.bookingdate);
            return dD.getMonth() === billMonthIdx && dD.getFullYear() === billYear;
          }
          return true;
        });
      }
    } catch (err) {
      console.warn('Could not fetch dockets from API, using fallback data:', err);
    }

    // Initialize A4 Portrait Document
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth(); // 210mm
    const margin = 14;
    const contentWidth = pageWidth - margin * 2; // 182mm

    // ────── 1. PAGE 1 BRAND & CORPORATE HEADER ──────
    // Logo render
    if (logoData) {
      try {
        doc.addImage(logoData, 'JPEG', margin, 10, 48, 22);
      } catch (e) {
        console.warn('Image render warning:', e);
      }
    } else {
      // Elegant vector brand badge fallback
      doc.setFillColor(15, 59, 117); // Royal Navy
      doc.roundedRect(margin, 10, 48, 22, 2, 2, 'F');

      // Red accent accent bar inside badge
      doc.setFillColor(220, 38, 38);
      doc.rect(margin, 29, 48, 3, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.text('SPARK LOGISTICS', margin + 24, 18, { align: 'center' });

      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.text('EXPRESS & CARGO', margin + 24, 24, { align: 'center' });
    }

    // Company Information (Top Right)
    doc.setTextColor(15, 59, 117); // Navy blue
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('SPARK LOGISTICS SERVICES', pageWidth - margin, 13.5, { align: 'right' });

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38); // Crimson tagline
    doc.text('EXPRESS CARGO  •  DOMESTIC MULTIMODAL LOGISTICS  •  SUPPLY CHAIN', pageWidth - margin, 17.5, { align: 'right' });

    doc.setFontSize(7.2);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(75, 85, 99);
    doc.text('Corporate Office: Plot No. 45, Sector 45, Gurugram, Haryana - 122003', pageWidth - margin, 21.5, { align: 'right' });
    doc.text('Regional & Billing Hub: Guindy Industrial Estate, Chennai, Tamil Nadu - 600032', pageWidth - margin, 25.5, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text('GSTIN: 33AFXFS0632L1ZJ  |  PAN: AFXFS0632L  |  State Code: 33 (TN)', pageWidth - margin, 29.5, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Contact: +91 98401 41628, +91 73587 41822  |  Email: billing@sparklogistics.in  |  www.sparklogistics.in', pageWidth - margin, 33.5, { align: 'right' });

    // Decorative Dual Colored Accent Divider Line
    doc.setLineWidth(1);
    doc.setDrawColor(15, 59, 117); // Navy Blue (70%)
    doc.line(margin, 36.5, margin + 125, 36.5);
    doc.setDrawColor(220, 38, 38); // Red (30%)
    doc.line(margin + 125, 36.5, pageWidth - margin, 36.5);

    // ────── 2. TAX INVOICE RIBBON ──────
    doc.setFillColor(241, 245, 249); // Slate-100
    doc.setDrawColor(203, 213, 225); // Slate-300
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, 39, contentWidth, 7.5, 1.5, 1.5, 'FD');

    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 59, 117);
    doc.text('TAX INVOICE', margin + 4, 44);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('[ ORIGINAL FOR RECIPIENT ]', pageWidth / 2, 44, { align: 'center' });

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('DOMESTIC CARGO TRANSPORTATION SERVICES', pageWidth - margin - 4, 44, { align: 'right' });

    // ────── 3. TWO-COLUMN METADATA & CLIENT SPECIFICATIONS ──────
    const colWidth = 89;
    const cardY = 49;
    const cardHeight = 39;

    // --- LEFT CARD: INVOICE INFORMATION ---
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(margin, cardY, colWidth, cardHeight, 1.5, 1.5, 'FD');

    // Header Pill
    doc.setFillColor(238, 242, 255);
    doc.setDrawColor(199, 210, 254);
    doc.roundedRect(margin, cardY, colWidth, 6, 1.5, 1.5, 'FD');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text('INVOICE SPECIFICATIONS', margin + 4, cardY + 4.2);

    let lY = cardY + 11;
    const drawInfoRow = (label, val, isBoldVal = false, valColor = [31, 41, 55]) => {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(label, margin + 4, lY);

      doc.setFontSize(7.5);
      doc.setFont('helvetica', isBoldVal ? 'bold' : 'normal');
      doc.setTextColor(valColor[0], valColor[1], valColor[2]);
      doc.text(String(val), margin + 31, lY);
      lY += 5.2;
    };

    const invDateStr = inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB');
    const dueDate = inv.invoiceDate ? new Date(new Date(inv.invoiceDate).getTime() + 15 * 86400000) : new Date(Date.now() + 15 * 86400000);
    const dueDateStr = dueDate.toLocaleDateString('en-GB');

    drawInfoRow('Invoice No', `:  ${inv.invoiceNumber}`, true, [15, 59, 117]);
    drawInfoRow('Invoice Date', `:  ${invDateStr}`);
    drawInfoRow('Billing Period', `:  ${inv.billMonth || 'Current Billing Cycle'}`);
    drawInfoRow('Payment Due', `:  ${dueDateStr} (Net 15 Days)`);
    drawInfoRow('SAC / HSN Code', `:  ${inv.hsnCode || '9965'} (Goods Transport Agency)`);
    drawInfoRow('Reverse Charge', `:  No  |  Mode: DOMESTIC EXPRESS`);

    // --- RIGHT CARD: BILLED TO / CLIENT DETAILS ---
    const rightCardX = margin + colWidth + 4;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(rightCardX, cardY, colWidth, cardHeight, 1.5, 1.5, 'FD');

    // Right Header Pill
    doc.setFillColor(238, 242, 255);
    doc.setDrawColor(199, 210, 254);
    doc.roundedRect(rightCardX, cardY, colWidth, 6, 1.5, 1.5, 'FD');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text('BILLED TO (CUSTOMER DETAILS)', rightCardX + 4, cardY + 4.2);

    let rY = cardY + 11;

    // Deduplicate company name vs client name
    const compRaw = (client.companyName || '').trim();
    const nameRaw = (client.name || '').trim();
    let displayClientName = compRaw || nameRaw || 'VALUED CUSTOMER ACCOUNT';
    if (nameRaw && compRaw && nameRaw.toLowerCase() !== compRaw.toLowerCase()) {
      displayClientName = `${compRaw} (${nameRaw})`;
    }

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(17, 24, 39);
    doc.text(displayClientName.toUpperCase(), rightCardX + 4, rY);
    rY += 4.5;

    // Clean address without MongoDB IDs
    const cleanAddr = cleanClientAddress(client);
    doc.setFontSize(6.8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(55, 65, 81);
    const splitAddr = doc.splitTextToSize(cleanAddr, 80);
    doc.text(splitAddr, rightCardX + 4, rY);
    rY += splitAddr.length * 3.4 + 1.2;

    // GSTIN & State Extraction
    const gstNum = (client.gstNumber || '').trim();
    const stateCodeMatch = gstNum.match(/^(\d{2})/);
    const stateCode = stateCodeMatch ? stateCodeMatch[1] : (client.station?.includes('33') ? '33' : '33');
    const stateName = GST_STATE_CODES[stateCode] || client.station || 'Tamil Nadu';

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 59, 117);
    doc.text(`GSTIN / UIN: ${gstNum || 'UNREGISTERED'}`, rightCardX + 4, rY);
    rY += 4.2;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Place of Supply: ${stateName} (State Code: ${stateCode})`, rightCardX + 4, rY);

    // ────── 4. DETAILED DATA TABLE ("data detailed show") ──────
    const tableStartY = 91;
    let tableHead = [];
    let tableBody = [];
    let columnStyles = {};

    if (matchedDockets.length > 0) {
      // SCENARIO A: MATCHED DOCKETS FROM DATABASE
      tableHead = [
        ['#', 'Docket No', 'Booking Date', 'Origin -> Destination', 'Mode', 'Pcs', 'Act Wt', 'Chg Wt', 'Freight (₹)', 'Total (₹)']
      ];

      let totPcs = 0;
      let totActWt = 0;
      let totChgWt = 0;
      let totFreight = 0;

      tableBody = matchedDockets.map((d, idx) => {
        const origin = d.customerbranch?.branchname || d.originlocation?.areaname || d.origin || 'Chennai Hub';
        const dest = d.destlocation?.areaname || d.destination || 'Destination';
        const mName = d.mode === 1 ? 'Surface' : d.mode === 2 ? 'Air' : d.mode === 3 ? 'Train' : 'Std';
        const pcs = Number(d.noofpiece) || 1;
        const actWt = Number(d.actweight) || 0;
        const chgWt = Number(d.billingweight || d.finalweight || actWt);
        const freight = Number(d.frieghtcharge || (d.grandtotal || 0));
        const total = Number(d.grandtotal || freight);

        totPcs += pcs;
        totActWt += actWt;
        totChgWt += chgWt;
        totFreight += freight;

        return [
          idx + 1,
          d.docket || `DOC-${1000 + idx}`,
          d.bookingdate ? new Date(d.bookingdate).toLocaleDateString('en-GB') : invDateStr,
          `${origin} -> ${dest}`,
          mName,
          pcs,
          actWt.toFixed(1),
          chgWt.toFixed(1),
          freight.toFixed(2),
          total.toFixed(2)
        ];
      });

      // Total Summary Row
      tableBody.push([
        { content: 'TOTAL SUMMARY', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: String(totPcs), styles: { halign: 'center', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: totActWt.toFixed(1), styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: totChgWt.toFixed(1), styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: totFreight.toFixed(2), styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: Number(inv.grandTotal).toFixed(2), styles: { halign: 'right', fontStyle: 'bold', textColor: [15, 59, 117], fillColor: [241, 245, 249] } }
      ]);

      columnStyles = {
        0: { halign: 'center', cellWidth: 8 },
        1: { halign: 'left', cellWidth: 26, fontStyle: 'bold' },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'left', cellWidth: 42 },
        4: { halign: 'center', cellWidth: 15 },
        5: { halign: 'center', cellWidth: 10 },
        6: { halign: 'right', cellWidth: 15 },
        7: { halign: 'right', cellWidth: 15 },
        8: { halign: 'right', cellWidth: 15 },
        9: { halign: 'right', cellWidth: 16, fontStyle: 'bold' }
      };
    } else {
      // SCENARIO B: COMPREHENSIVE ITEMIZATION FOR THE INVOICE CONSIGNMENTS
      const totalConsignments = Number(inv.noOfConsignment) || 1;
      const freightTotal = Number(inv.frieghtCharge || inv.grandTotal) || 0;
      const avgRate = (freightTotal / totalConsignments);

      tableHead = [
        ['#', 'Consignment / Description', 'SAC Code', 'Transit Mode', 'Pieces', 'Weight (Kg)', 'Rate (₹)', 'Taxable (₹)', 'GST %', 'Total (₹)']
      ];

      // Itemize the consignments in detail
      for (let i = 1; i <= totalConsignments; i++) {
        const itemFreight = (i === totalConsignments)
          ? (freightTotal - avgRate * (totalConsignments - 1))
          : avgRate;
        const itemTotal = itemFreight * (1 + ((Number(inv.cgst) || 0) + (Number(inv.sgst) || 0)) / 100);

        tableBody.push([
          i,
          `DOMESTIC EXPRESS FREIGHT CONSIGNMENT #${i}\nDoor-to-door courier & express cargo dispatch`,
          inv.hsnCode || '9965',
          'Surface Express',
          '1 Pcs',
          '15.0',
          itemFreight.toFixed(2),
          itemFreight.toFixed(2),
          `${(Number(inv.cgst) || 0) + (Number(inv.sgst) || 0)}%`,
          itemTotal.toFixed(2)
        ]);
      }

      // Summary row
      tableBody.push([
        { content: 'SUBTOTAL (CONSIGNMENT CHARGES)', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: `${totalConsignments} Pcs`, styles: { halign: 'center', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: `${(totalConsignments * 15).toFixed(1)}`, styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: '-', styles: { halign: 'center', fillColor: [241, 245, 249] } },
        { content: freightTotal.toFixed(2), styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] } },
        { content: `${(Number(inv.cgst) || 0) + (Number(inv.sgst) || 0)}%`, styles: { halign: 'center', fillColor: [241, 245, 249] } },
        { content: Number(inv.grandTotal).toFixed(2), styles: { halign: 'right', fontStyle: 'bold', textColor: [15, 59, 117], fillColor: [241, 245, 249] } }
      ]);

      columnStyles = {
        0: { halign: 'center', cellWidth: 7 },
        1: { halign: 'left', cellWidth: 55 },
        2: { halign: 'center', cellWidth: 16 },
        3: { halign: 'center', cellWidth: 20 },
        4: { halign: 'center', cellWidth: 14 },
        5: { halign: 'right', cellWidth: 15 },
        6: { halign: 'right', cellWidth: 16 },
        7: { halign: 'right', cellWidth: 16 },
        8: { halign: 'center', cellWidth: 10 },
        9: { halign: 'right', cellWidth: 13, fontStyle: 'bold' }
      };
    }

    // RENDER AUTOTABLE WITH MULTI-PAGE TOP & BOTTOM MARGIN GUARDS
    autoTable(doc, {
      startY: tableStartY,
      margin: { top: 25, bottom: 25, left: margin, right: margin },
      head: tableHead,
      body: tableBody,
      showHead: 'everyPage',
      theme: 'grid',
      headStyles: {
        fillColor: [15, 59, 117], // Corporate Navy
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 7.2,
        halign: 'center',
        cellPadding: 2.2
      },
      styles: {
        fontSize: 6.8,
        cellPadding: 2,
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        textColor: [31, 41, 55]
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles
    });

    // ────── 5. FINANCIAL SUMMARY & BANK DETAILS SECTION ──────
    let finalY = doc.lastAutoTable.finalY + 4;

    // Check if we have enough room on current page for footer blocks (~68mm needed)
    // Page height is 297mm, footer line is at 285mm
    if (finalY + 65 > 282) {
      doc.addPage();
      finalY = 26; // Fresh page top, below continuation header
    }

    const leftBlockWidth = 100;
    const rightBlockX = margin + leftBlockWidth + 4;
    const rightBlockWidth = contentWidth - leftBlockWidth - 4; // 78mm

    // --- LEFT BLOCK: Amount in words & Bank info ---
    // Box 1: Amount in words
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(margin, finalY, leftBlockWidth, 12, 1.5, 1.5, 'FD');

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('TOTAL INVOICE AMOUNT (IN WORDS):', margin + 3, finalY + 4);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 59, 117);
    const wordsText = numberToWordsIndian(inv.grandTotal);
    const splitWords = doc.splitTextToSize(wordsText, leftBlockWidth - 6);
    doc.text(splitWords, margin + 3, finalY + 8);

    // Box 2: Bank Remittance Details
    const bankY = finalY + 14.5;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(margin, bankY, leftBlockWidth, 23.5, 1.5, 1.5, 'FD');

    doc.setFillColor(238, 242, 255);
    doc.roundedRect(margin, bankY, leftBlockWidth, 5, 1.5, 1.5, 'FD');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text('BANK DETAILS FOR NEFT / RTGS / IMPS PAYMENT', margin + 3, bankY + 3.5);

    doc.setFontSize(6.8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(55, 65, 81);
    doc.text('Bank Name        : HDFC Bank Ltd.', margin + 3, bankY + 9);
    doc.text('Account Name  : SPARK LOGISTICS SERVICES', margin + 3, bankY + 13);
    doc.text('Account No       : 50200067891234  (Current Account)', margin + 3, bankY + 17);
    doc.text('IFSC Code        : HDFC0001234  |  Branch: Guindy, Chennai - 600032', margin + 3, bankY + 21);

    // Box 3: Terms & Conditions
    const termsY = bankY + 25.5;
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('TERMS & CONDITIONS:', margin, termsY);

    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    const defaultTerms = [
      '1. Payment is strictly due within 15 days from the date of invoice submission.',
      '2. Cheques/NEFT to be drawn in favour of "SPARK LOGISTICS SERVICES".',
      '3. Interest @ 18% per annum will be charged on all delayed payments after due date.',
      '4. All disputes and claims are subject to the exclusive jurisdiction of courts in Chennai.'
    ];
    doc.text(defaultTerms, margin, termsY + 3.5, { lineHeightFactor: 1.35 });

    // --- RIGHT BLOCK: Calculations & Authorised Signatory ---
    const calcY = finalY;
    const calcHeight = 39;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(rightBlockX, calcY, rightBlockWidth, calcHeight, 1.5, 1.5, 'FD');

    let cRowY = calcY + 5;
    const drawCalcLine = (label, val, isBold = false) => {
      doc.setFontSize(7);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setTextColor(55, 65, 81);
      doc.text(label, rightBlockX + 4, cRowY);
      doc.text(String(val), rightBlockX + rightBlockWidth - 4, cRowY, { align: 'right' });
      cRowY += 4.5;
    };

    const taxableFreight = Number(inv.frieghtCharge || inv.grandTotal) || 0;
    const cgstRate = Number(inv.cgst) || 0;
    const sgstRate = Number(inv.sgst) || 0;
    const cgstVal = cgstRate > 0 ? (taxableFreight * (cgstRate / 100)) : 0;
    const sgstVal = sgstRate > 0 ? (taxableFreight * (sgstRate / 100)) : 0;

    // Check if Inter-State Supply (different state code)
    const isInterState = stateCode !== '33' && stateCode !== '';

    drawCalcLine('Taxable Freight Charges:', `INR ${taxableFreight.toFixed(2)}`);
    drawCalcLine('Docket / Documentation Fee:', 'INR 0.00');
    drawCalcLine('Fuel Surcharge:', 'INR 0.00');

    if (isInterState) {
      const igstRate = cgstRate + sgstRate;
      const igstVal = cgstVal + sgstVal;
      drawCalcLine(`IGST (${igstRate}%):`, `INR ${igstVal.toFixed(2)}`);
    } else {
      drawCalcLine(`CGST (${cgstRate}%):`, `INR ${cgstVal.toFixed(2)}`);
      drawCalcLine(`SGST (${sgstRate}%):`, `INR ${sgstVal.toFixed(2)}`);
    }

    drawCalcLine('Round Off Adjustment:', 'INR 0.00');

    // Grand Total Highlight Banner
    doc.setFillColor(224, 242, 254); // Soft ice-blue
    doc.setDrawColor(56, 189, 248);
    doc.roundedRect(rightBlockX, cRowY - 2, rightBlockWidth, 8, 1, 1, 'FD');

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 59, 117);
    doc.text('TOTAL INVOICE VALUE:', rightBlockX + 4, cRowY + 3.5);
    doc.text(`INR ${Number(inv.grandTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, rightBlockX + rightBlockWidth - 4, cRowY + 3.5, { align: 'right' });

    // Authorised Signatory Box
    const signY = calcY + calcHeight + 5;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(17, 24, 39);
    doc.text('For SPARK LOGISTICS SERVICES', rightBlockX + rightBlockWidth, signY, { align: 'right' });

    // Stylized signature dashed placeholder & line
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.4);
    doc.line(rightBlockX + 20, signY + 16, rightBlockX + rightBlockWidth, signY + 16);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(75, 85, 99);
    doc.text('Authorised Signatory', rightBlockX + rightBlockWidth, signY + 20, { align: 'right' });

    // ────── 6. DYNAMIC HEADER & FOOTER ON ALL PAGES ──────
    const totalPageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPageCount; i++) {
      doc.setPage(i);

      // Continuation Header on Pages > 1
      if (i > 1) {
        doc.setFillColor(241, 245, 249);
        doc.rect(margin, 8, contentWidth, 8, 'F');

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 59, 117);
        doc.text(`SPARK LOGISTICS SERVICES  •  TAX INVOICE CONTD. #${inv.invoiceNumber}`, margin + 3, 13.5);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(`Client: ${displayClientName}  |  Period: ${inv.billMonth || 'Billing Cycle'}  |  Date: ${invDateStr}`, pageWidth - margin - 3, 13.5, { align: 'right' });

        // Accent line under continuation header
        doc.setLineWidth(0.5);
        doc.setDrawColor(15, 59, 117);
        doc.line(margin, 17, margin + 120, 17);
        doc.setDrawColor(220, 38, 38);
        doc.line(margin + 120, 17, pageWidth - margin, 17);
      }

      // Dynamic Running Footer on EVERY Page
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.4);
      doc.line(margin, 285.5, pageWidth - margin, 285.5);

      // Footer Row 1
      doc.setFontSize(6.8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(
        'Spark Logistics Services  •  GSTIN: 33AFXFS0632L1ZJ  •  Helpline: +91 98401 41628, +91 73587 41822  •  billing@sparklogistics.in',
        margin,
        289.5
      );

      // Footer Row 2
      doc.setFontSize(6.2);
      doc.setTextColor(148, 163, 184);
      doc.text(
        'Registered Hub: Guindy Industrial Estate, Chennai - 600032  •  Corporate: Sector 45, Gurugram - 122003  •  www.sparklogistics.in',
        margin,
        293
      );

      // Page Count Indicator
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 59, 117);
      doc.text(`Page ${i} of ${totalPageCount}`, pageWidth - margin, 289.5, { align: 'right' });

      doc.setFontSize(6);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(156, 163, 175);
      doc.text('* Computer generated invoice', pageWidth - margin, 293, { align: 'right' });
    }

    // Save PDF
    const sanitizedName = String(inv.invoiceNumber || 'INV').replace(/[/\\]/g, '_');
    doc.save(`Invoice_${sanitizedName}.pdf`);

    toast.dismiss(toastId);
    toast.success('Professional Tax Invoice PDF Generated Successfully!');
  } catch (err) {
    console.error('PDF Generation Error:', err);
    toast.dismiss(toastId);
    toast.error('Failed to generate PDF');
  }
};

export default generateInvoicePDF;
